import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NoticeBoard from './NoticeBoard';
import Events from './Events';
import BannerSlider from './BannerSlider';
import DepartmentCardImage from './DepartmentCardImage';
import { useGetBannersQuery } from '../redux/bannerApi';
import { getBannerSlides } from '../utils/bannerSlides';
import {
  departmentImageSrc,
  preloadDepartmentImages,
} from '../utils/departmentImages';
import { useGetActiveDepartmentsQuery } from '../redux/employeeApi';

const FALLBACK_FEATURE_CARDS = [
  { title: 'People Capital Processes', image: 'office' },
  { title: 'Employee Training', image: 'training' },
  { title: 'Production Operations', image: 'factory' },
  { title: 'Business Analysis', image: 'meeting' },
  { title: 'Dispatch & Logistics', image: 'delivery' },
  { title: 'Employee Recognition', image: 'handshake' },
];

function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [heroSlide, setHeroSlide] = useState(0);
  const [firstBannerReady, setFirstBannerReady] = useState(false);
  const { data: bannersRes } = useGetBannersQuery();
  const { data: departmentsRes } = useGetActiveDepartmentsQuery();

  const heroSlides = useMemo(
    () => getBannerSlides(bannersRes, 'landing', { size: 'large' }),
    [bannersRes]
  );

  const featureCards = useMemo(() => {
    const list = departmentsRes?.data || departmentsRes || [];
    if (Array.isArray(list) && list.length > 0) {
      return list.map((d) => ({
        id: d.id,
        title: d.name,
        imageSrc: departmentImageSrc(d.image, { size: 'thumb' }),
      }));
    }
    return FALLBACK_FEATURE_CARDS.map((c, idx) => ({
      id: idx,
      title: c.title,
      imageSrc: '',
    }));
  }, [departmentsRes]);

  useEffect(() => {
    const sources = featureCards.map((card) => card.imageSrc).filter(Boolean);
    preloadDepartmentImages(sources, 6);
  }, [featureCards]);

  useEffect(() => {
    if (!heroSlides.length || !firstBannerReady) return undefined;
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [heroSlides.length, firstBannerReady]);

  useEffect(() => {
    setHeroSlide((prev) => (heroSlides.length ? prev % heroSlides.length : 0));
  }, [heroSlides.length]);

  useEffect(() => {
    if (location.hash !== '#notice-board' && location.hash !== '#workspace') return;
    const targetId = location.hash.slice(1);
    const el = document.getElementById(targetId);
    if (!el) return;
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen bg-[#F7E5D5]">
      <section>
        <BannerSlider
          key="landing-banner"
          scope="landing"
          slides={heroSlides}
          currentSlide={heroSlide}
          onSelectSlide={setHeroSlide}
          onFirstSlideReady={setFirstBannerReady}
          variant="landing"
          alt="Banner"
        />
      </section>

      <section
        id="workspace"
        className="scroll-mt-20 bg-[#F7E5D5] px-4 py-6 sm:px-6 sm:py-8"
      >
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-6">
          {featureCards.map((card, index) => (
            <button
              key={card.id}
              type="button"
              onClick={() => navigate(`/department/${card.id}`)}
              className="overflow-hidden rounded-xl bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              aria-label={`Open ${card.title} department`}
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-[#e8e4df]">
                {card.imageSrc ? (
                  <DepartmentCardImage
                    src={card.imageSrc}
                    alt={card.title}
                    priority={index < 6}
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-gray-200 to-gray-100" />
                )}
              </div>
              <h3 className="line-clamp-2 min-h-[2.75rem] px-2 py-3 text-center text-xs font-semibold leading-snug text-gray-900 sm:min-h-[3rem] sm:px-4 sm:text-sm">
                {card.title}
              </h3>
            </button>
          ))}
        </div>
      </section>

      <section
        id="notice-board"
        className="scroll-mt-20 bg-[#F7E5D5] px-4 pb-8 sm:px-6 sm:pb-10"
      >
        <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          <NoticeBoard />
          <Events />
        </div>
      </section>
    </div>
  );
}

export default LandingPage;
